import { ApiClient } from "./api-client";
import { SseClient } from "./sse-client";
import { detectProductImage } from "./product-detector";
import { compressImage, isValidImageFile } from "./image-utils";
import { getStyles } from "./styles";
import type { ButtonStyles, ModalStyles, VtoUpdate } from "./types";
import { createT, type TranslateFn } from "./i18n";
import { startSdkClarity, tagSdkClaritySession } from "./clarity";

type ViewState = "idle" | "upload" | "processing" | "result" | "error";

function createSdkOpenId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `ps_open_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function isTransientResultImageUrl(url?: string | null): boolean {
  const clean = String(url || "").trim();
  return clean.startsWith("data:") || clean.startsWith("blob:");
}

// SVG icons as template strings
const ICONS = {
  camera: `<svg viewBox="0 0 24 24"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>`,
  upload: `<svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>`,
  x: `<svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
  alert: `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`,
};

export class PrimeStyleTryon extends HTMLElement {
  private shadow: ShadowRoot;
  private apiClient: ApiClient | null = null;
  private sseClient: SseClient | null = null;
  private sseUnsubscribe: (() => void) | null = null;

  private state: ViewState = "idle";
  private selectedFile: File | null = null;
  private previewUrl: string | null = null;
  private resultImageUrl: string | null = null;
  private transientResultImageUrl: string | null = null;
  private errorMessage: string | null = null;
  private currentJobId: string | null = null;
  private sdkOpenId: string | null = null;
  private productImageUrl: string | null = null;
  private productViewReportedFor: string | null = null;

  // i18n
  private t: TranslateFn = createT();

  // Custom style configs
  private buttonStyles: ButtonStyles = {};
  private modalStyles: ModalStyles = {};

  static get observedAttributes(): string[] {
    return [
      "api-key",
      "api-url",
      "product-image",
      "button-text",
      "locale",
      "show-powered-by",
      "clarity-project-id",
      "button-styles",
      "modal-styles",
    ];
  }

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.init();
    this.render();
  }

  disconnectedCallback(): void {
    this.cleanup();
  }

  attributeChangedCallback(name: string, _old: string, val: string): void {
    if (name === "api-key" || name === "api-url") {
      this.initApi();
    }
    if (name === "locale") {
      this.t = createT(val || undefined);
    }
    if (name === "product-image") {
      this.productImageUrl = val;
    }
    if (name === "button-styles") {
      try { this.buttonStyles = JSON.parse(val); } catch { /* ignore */ }
    }
    if (name === "modal-styles") {
      try { this.modalStyles = JSON.parse(val); } catch { /* ignore */ }
    }
    if (this.isConnected) this.render();
  }

  // ── Public API ──────────────────────────────

  /** Configure button appearance programmatically */
  setButtonStyles(styles: ButtonStyles): void {
    this.buttonStyles = { ...this.buttonStyles, ...styles };
    this.applyButtonStyles();
  }

  /** Configure modal appearance programmatically */
  setModalStyles(styles: ModalStyles): void {
    this.modalStyles = { ...this.modalStyles, ...styles };
    this.applyModalStyles();
  }

  private savedScrollY = 0;

  private lockBodyScroll(): void {
    this.savedScrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    window.scrollTo(0, this.savedScrollY);
  }

  /** Open the try-on modal */
  open(): void {
    this.sdkOpenId = createSdkOpenId();
    this.reportEvent("SDK_OPENED", { metadata: { component: "PrimeStyleTryonElement" } });
    this.state = "upload";
    this.tagClarity("upload");
    this.lockBodyScroll();
    this.render();
    this.emit("ps:open");
  }

  /** Close the try-on modal */
  close(): void {
    this.state = "idle";
    this.sdkOpenId = null;
    this.unlockBodyScroll();
    this.resetUpload();
    this.render();
    this.emit("ps:close");
  }

  /** Detect product image from the current page */
  detectProduct(): string | null {
    const image = detectProductImage();
    if (image) {
      this.productImageUrl = image;
      this.emit("ps:product-detected", { imageUrl: image });
    }
    return image;
  }

  // ── Private ─────────────────────────────────

  private init(): void {
    // Parse attribute-based styles
    const btnStylesAttr = this.getAttribute("button-styles");
    if (btnStylesAttr) {
      try { this.buttonStyles = JSON.parse(btnStylesAttr); } catch { /* ignore */ }
    }
    const modalStylesAttr = this.getAttribute("modal-styles");
    if (modalStylesAttr) {
      try { this.modalStyles = JSON.parse(modalStylesAttr); } catch { /* ignore */ }
    }

    // Init locale
    const localeAttr = this.getAttribute("locale");
    if (localeAttr) this.t = createT(localeAttr);

    this.productImageUrl = this.getAttribute("product-image") || null;

    // Auto-detect product image if not provided
    if (!this.productImageUrl) {
      this.productImageUrl = detectProductImage();
      if (this.productImageUrl) {
        this.emit("ps:product-detected", { imageUrl: this.productImageUrl });
      }
    }

    this.initApi();
    this.reportProductView();
  }

  private initApi(): void {
    const apiKey = this.getAttribute("api-key") || undefined;
    const apiUrl = this.getAttribute("api-url") || undefined;
    this.apiClient = new ApiClient(apiKey, apiUrl);
    this.sseClient = null;
  }

  private cleanup(): void {
    if (this.state !== "idle") {
      this.unlockBodyScroll();
    }
    if (this.sseUnsubscribe) {
      this.sseUnsubscribe();
      this.sseUnsubscribe = null;
    }
    if (this.sseClient) {
      this.sseClient.disconnect();
      this.sseClient = null;
    }
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  private emit(name: string, detail?: unknown): void {
    this.dispatchEvent(
      new CustomEvent(name, { bubbles: true, composed: true, detail })
    );
  }

  private get eventProductContext(): {
    productId?: string;
    productTitle?: string;
    productUrl?: string;
  } {
    return {
      productId: this.getAttribute("product-id") || this.productImageUrl || undefined,
      productTitle: this.getAttribute("product-title") || document.title || undefined,
      productUrl: typeof window !== "undefined" ? window.location.href : undefined,
    };
  }

  private reportProductView(): void {
    const key = `${this.eventProductContext.productId || ""}|${this.eventProductContext.productUrl || ""}`;
    if (!key.trim() || this.productViewReportedFor === key) return;
    this.productViewReportedFor = key;
    this.tagClarity("idle");
    this.reportEvent("PRODUCT_VIEW", { metadata: { component: "PrimeStyleTryonElement" } });
  }

  private tagClarity(view: ViewState | "idle", jobId?: string | null): void {
    const context = {
      projectId: this.getAttribute("clarity-project-id"),
      sdkOpenId: this.sdkOpenId,
      ...this.eventProductContext,
      view,
      jobId,
      source: "custom-element-sdk",
    };
    startSdkClarity(context);
    tagSdkClaritySession(context);
  }

  private reportEvent(
    eventType: Parameters<ApiClient["reportEvent"]>[0]["eventType"],
    input: Omit<Parameters<ApiClient["reportEvent"]>[0], "eventType" | "productId" | "productTitle" | "productUrl"> = {},
  ): void {
    void this.apiClient?.reportEvent({
      eventType,
      sdkOpenId: this.sdkOpenId ?? undefined,
      ...this.eventProductContext,
      ...input,
      metadata: {
        ...(this.sdkOpenId ? { sdkOpenId: this.sdkOpenId } : {}),
        ...(input.metadata ?? {}),
      },
    }).catch(() => {});
  }

  private reportClientError(
    input: Omit<Parameters<ApiClient["reportClientError"]>[0], "productId" | "productTitle" | "productUrl">,
  ): void {
    void this.apiClient?.reportClientError({
      ...this.eventProductContext,
      ...input,
    }).catch(() => {});
  }

  private get buttonText(): string {
    return this.getAttribute("button-text") || this.t("Virtual Try-On");
  }

  private get showPoweredBy(): boolean {
    const attr = this.getAttribute("show-powered-by");
    return attr !== "false";
  }

  // ── Rendering ───────────────────────────────

  private render(): void {
    this.shadow.innerHTML = "";

    const style = document.createElement("style");
    style.textContent = getStyles();
    this.shadow.appendChild(style);

    // Button (always visible)
    const button = this.createButton();
    this.shadow.appendChild(button);

    // Modal overlay
    if (this.state !== "idle") {
      const overlay = this.createModal();
      this.shadow.appendChild(overlay);
      // Trigger open animation
      requestAnimationFrame(() => overlay.classList.add("ps-open"));
    }

    this.applyButtonStyles();
    this.applyModalStyles();
  }

  private createButton(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = "ps-button";
    btn.innerHTML = `${ICONS.camera}<span>${this.buttonText}</span>`;
    btn.addEventListener("click", () => this.open());
    return btn;
  }

  private createModal(): HTMLDivElement {
    const overlay = document.createElement("div");
    overlay.className = "ps-overlay";
    overlay.setAttribute("data-clarity-unmask", "true");
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this.close();
    });

    const modal = document.createElement("div");
    modal.className = "ps-modal";
    modal.setAttribute("data-clarity-unmask", "true");

    // Header
    const header = document.createElement("div");
    header.className = "ps-header";
    header.innerHTML = `
      <span class="ps-header-title">${this.t("Virtual Try-On")}</span>
    `;
    const closeBtn = document.createElement("button");
    closeBtn.className = "ps-close";
    closeBtn.innerHTML = ICONS.x;
    closeBtn.addEventListener("click", () => this.close());
    header.appendChild(closeBtn);
    modal.appendChild(header);

    // Body
    const body = document.createElement("div");
    body.className = "ps-body";
    body.setAttribute("data-clarity-unmask", "true");

    switch (this.state) {
      case "upload":
        body.appendChild(this.createUploadView());
        break;
      case "processing":
        body.appendChild(this.createProcessingView());
        break;
      case "result":
        body.appendChild(this.createResultView());
        break;
      case "error":
        body.appendChild(this.createErrorView());
        break;
    }

    modal.appendChild(body);

    // Powered by
    if (this.showPoweredBy) {
      const powered = document.createElement("div");
      powered.className = "ps-powered";
      powered.innerHTML = `${this.t("Powered by")} <a href="https://myaifitting.com" target="_blank" rel="noopener">PrimeStyle AI</a>`;
      modal.appendChild(powered);
    }

    overlay.appendChild(modal);
    return overlay;
  }

  private createUploadView(): DocumentFragment {
    const frag = document.createDocumentFragment();

    if (this.selectedFile && this.previewUrl) {
      // Show preview
      const preview = document.createElement("div");
      preview.className = "ps-preview";

      const img = document.createElement("img");
      img.src = this.previewUrl;
      img.alt = this.t("Your photo");
      preview.appendChild(img);

      const removeBtn = document.createElement("button");
      removeBtn.className = "ps-preview-remove";
      removeBtn.textContent = "\u00d7";
      removeBtn.addEventListener("click", () => {
        this.resetUpload();
        this.render();
      });
      preview.appendChild(removeBtn);

      frag.appendChild(preview);

      // Submit button
      const submit = document.createElement("button");
      submit.className = "ps-submit";
      submit.textContent = this.t("Try It On");
      submit.addEventListener("click", () => this.handleSubmit());
      frag.appendChild(submit);
    } else {
      // Upload zone
      const zone = document.createElement("div");
      zone.className = "ps-upload-zone";

      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/jpeg,image/png,image/webp";
      input.addEventListener("change", (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) this.handleFileSelect(file);
      });

      zone.innerHTML = `
        <svg class="ps-upload-icon" viewBox="0 0 24 24">${ICONS.upload.replace(/<\/?svg[^>]*>/g, "")}</svg>
        <p class="ps-upload-text">${this.t("Drop your photo here or click to upload")}</p>
        <p class="ps-upload-hint">${this.t("JPEG, PNG or WebP (max 10MB)")}</p>
      `;
      zone.appendChild(input);

      zone.addEventListener("click", () => input.click());
      zone.addEventListener("dragover", (e) => {
        e.preventDefault();
        zone.classList.add("ps-drag-over");
      });
      zone.addEventListener("dragleave", () => {
        zone.classList.remove("ps-drag-over");
      });
      zone.addEventListener("drop", (e) => {
        e.preventDefault();
        zone.classList.remove("ps-drag-over");
        const file = e.dataTransfer?.files?.[0];
        if (file) this.handleFileSelect(file);
      });

      frag.appendChild(zone);
    }

    return frag;
  }

  private createProcessingView(): HTMLDivElement {
    const div = document.createElement("div");
    div.className = "ps-processing";
    div.innerHTML = `
      <div class="ps-spinner"></div>
      <p class="ps-processing-text">${this.t("Generating virtual try-on...")}</p>
      <p class="ps-processing-sub">${this.t("This usually takes 15-20 seconds")}</p>
    `;
    return div;
  }

  private createResultView(): HTMLDivElement {
    const div = document.createElement("div");
    div.className = "ps-result";

    if (this.resultImageUrl) {
      const img = document.createElement("img");
      img.src = this.resultImageUrl;
      img.alt = this.t("Try-on result");
      div.appendChild(img);
    }

    const actions = document.createElement("div");
    actions.className = "ps-result-actions";

    const downloadBtn = document.createElement("button");
    downloadBtn.className = "ps-btn-download";
    downloadBtn.textContent = this.t("Download");
    downloadBtn.addEventListener("click", () => this.handleDownload());
    actions.appendChild(downloadBtn);

    const retryBtn = document.createElement("button");
    retryBtn.className = "ps-btn-retry";
    retryBtn.textContent = this.t("Try Another");
    retryBtn.addEventListener("click", () => {
      this.resetUpload();
      this.state = "upload";
      this.render();
    });
    actions.appendChild(retryBtn);

    div.appendChild(actions);
    return div;
  }

  private createErrorView(): HTMLDivElement {
    const div = document.createElement("div");
    div.className = "ps-error";
    div.innerHTML = `
      <svg class="ps-error-icon" viewBox="0 0 24 24">${ICONS.alert.replace(/<\/?svg[^>]*>/g, "")}</svg>
      <p class="ps-error-text">${this.errorMessage || this.t("Something went wrong")}</p>
    `;

    const retryBtn = document.createElement("button");
    retryBtn.className = "ps-submit";
    retryBtn.textContent = this.t("Try Again");
    retryBtn.addEventListener("click", () => {
      this.state = "upload";
      this.errorMessage = null;
      this.render();
    });
    div.appendChild(retryBtn);

    return div;
  }

  // ── Handlers ────────────────────────────────

  private handleFileSelect(file: File): void {
    if (!isValidImageFile(file)) {
      this.errorMessage = this.t("Please upload a JPEG, PNG, or WebP image.");
      this.state = "error";
      this.reportClientError({
        message: "Invalid image file type",
        code: "INVALID_FILE",
        component: "PrimeStyleTryonElement",
        view: "upload",
        severity: "low",
        metadata: { fileType: file.type, fileSize: file.size },
      });
      this.render();
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.errorMessage = this.t("Image must be under 10MB.");
      this.state = "error";
      this.reportClientError({
        message: "Image file too large",
        code: "FILE_TOO_LARGE",
        component: "PrimeStyleTryonElement",
        view: "upload",
        severity: "low",
        metadata: { fileType: file.type, fileSize: file.size },
      });
      this.render();
      return;
    }

    this.selectedFile = file;
    this.previewUrl = URL.createObjectURL(file);
    this.reportEvent("PHOTO_UPLOADED", {
      metadata: { fileType: file.type, fileSize: file.size, component: "PrimeStyleTryonElement" },
    });
    this.emit("ps:upload", { file });
    this.render();
  }

  private async handleSubmit(): Promise<void> {
    if (!this.selectedFile || !this.apiClient) {
      this.errorMessage = this.t("SDK not configured. Please provide an API key.");
      this.state = "error";
      this.reportClientError({
        message: this.errorMessage,
        code: !this.apiClient ? "SDK_NOT_CONFIGURED" : "PHOTO_MISSING",
        component: "PrimeStyleTryonElement",
        view: "upload",
        severity: !this.apiClient ? "high" : "low",
      });
      this.render();
      return;
    }

    if (!this.productImageUrl) {
      this.errorMessage = this.t("No product image found. Please set the product-image attribute.");
      this.state = "error";
      this.reportClientError({
        message: this.errorMessage,
        code: "PRODUCT_IMAGE_MISSING",
        component: "PrimeStyleTryonElement",
        view: "upload",
        severity: "medium",
      });
      this.render();
      return;
    }

    this.state = "processing";
    this.transientResultImageUrl = null;
    this.tagClarity("processing");
    this.render();

    try {
      const modelImage = await compressImage(this.selectedFile);

      const response = await this.apiClient.submitTryOn(
        modelImage,
        this.productImageUrl,
        undefined,
        undefined,
        { ...this.eventProductContext, sdkOpenId: this.sdkOpenId ?? undefined }
      );

      this.currentJobId = response.jobId;
      this.tagClarity("processing", response.jobId);
      this.emit("ps:processing", { jobId: response.jobId });

      // Subscribe to SSE updates for this job
      this.sseClient?.disconnect();
      this.sseClient = new SseClient(response.streamUrl || this.apiClient.getStreamUrl(response.jobId));
      this.sseUnsubscribe = this.sseClient.onJob(
        response.jobId,
        (update: VtoUpdate) => this.handleVtoUpdate(update)
      );

      // Fallback polling in case SSE misses
      this.startPolling(response.jobId);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : this.t("Failed to start try-on");
      this.errorMessage = message;
      this.state = "error";
      this.reportClientError({
        message,
        code: (err as { code?: string })?.code || "TRYON_SUBMIT_FAILED",
        stack: err instanceof Error ? err.stack : undefined,
        component: "PrimeStyleTryonElement",
        view: "processing",
        severity: "medium",
      });
      this.emit("ps:error", { message, code: (err as { code?: string })?.code });
      this.render();
    }
  }

  private handleVtoUpdate(update: VtoUpdate): void {
    const completedImageUrl = update.imageUrl || update.resultImageUrl || update.outputUrl || update.remoteOutputUrl || null;
    if (update.status === "completed" && completedImageUrl) {
      const isTransientCompletionImage = isTransientResultImageUrl(completedImageUrl);
      if (isTransientCompletionImage) {
        this.transientResultImageUrl = completedImageUrl;
        return;
      }
      // Prefer CDN URL over base64, but show whatever we get first
      if (
        this.state !== "result" ||
        (isTransientResultImageUrl(this.resultImageUrl) && !isTransientCompletionImage)
      ) {
        this.resultImageUrl = completedImageUrl;
        this.state = "result";
        this.tagClarity("result", update.galleryId);
        if (!isTransientCompletionImage) {
          this.emit("ps:complete", {
            jobId: update.galleryId,
            imageUrl: completedImageUrl,
          });
        }
        this.render();
      }
    } else if (update.status === "failed") {
      this.errorMessage = update.error || this.t("Try-on generation failed");
      this.state = "error";
      this.tagClarity("error", update.galleryId);
      this.reportClientError({
        message: this.errorMessage,
        code: "TRYON_GENERATION_FAILED",
        component: "PrimeStyleTryonElement",
        view: "processing",
        severity: "medium",
        jobId: update.galleryId,
      });
      this.emit("ps:error", { message: this.errorMessage });
      this.render();
    }
  }

  private startPolling(jobId: string): void {
    let attempts = 0;
    const maxAttempts = 240; // 8 minutes at 2s intervals
    const interval = setInterval(async () => {
      attempts++;
      const hasDurableResult = this.state === "result" && !isTransientResultImageUrl(this.resultImageUrl);
      if (attempts > maxAttempts || hasDurableResult || this.state === "idle") {
        if (attempts > maxAttempts && this.state === "processing") {
          if (this.transientResultImageUrl) {
            this.resultImageUrl = this.transientResultImageUrl;
            this.state = "result";
            this.tagClarity("result", jobId);
            this.render();
            clearInterval(interval);
            return;
          }
          this.handleVtoUpdate({
            galleryId: jobId,
            status: "failed",
            imageUrl: null,
            error: this.t("Try-on is taking longer than expected. Please try again."),
            timestamp: Date.now(),
          });
        }
        clearInterval(interval);
        return;
      }
      if (!this.apiClient) {
        clearInterval(interval);
        return;
      }

      try {
        const status = await this.apiClient.getStatus(jobId);
        const completedImageUrl = status.imageUrl || status.resultImageUrl || status.outputUrl || status.remoteOutputUrl || null;
        if (status.status === "completed" && completedImageUrl) {
          const isTransientCompletionImage = isTransientResultImageUrl(completedImageUrl);
          // Polling may replace an early SSE data URL with the durable CDN URL.
          if (this.state === "processing" || isTransientResultImageUrl(this.resultImageUrl)) {
            this.handleVtoUpdate({
              galleryId: jobId,
              status: "completed",
              imageUrl: completedImageUrl,
              error: null,
              timestamp: Date.now(),
            });
          }
          if (!isTransientCompletionImage) clearInterval(interval);
        } else if (status.status === "failed") {
          if (this.state === "processing") {
            this.handleVtoUpdate({
              galleryId: jobId,
              status: "failed",
              imageUrl: null,
              error: status.message,
              timestamp: Date.now(),
            });
          }
          clearInterval(interval);
        }
      } catch {
        // polling error — continue trying
      }
    }, 2000);
  }

  private handleDownload(): void {
    if (!this.resultImageUrl) return;

    const link = document.createElement("a");
    link.href = this.resultImageUrl;
    link.download = `primestyle-tryon-${Date.now()}.png`;
    link.target = "_blank";

    // For base64 URLs, download directly. For CDN URLs, open in new tab.
    if (this.resultImageUrl.startsWith("data:")) {
      link.click();
    } else {
      // Fetch and create blob for cross-origin download
      fetch(this.resultImageUrl)
        .then((r) => r.blob())
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          link.href = url;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 100);
        })
        .catch(() => {
          // Fallback: open in new tab
          window.open(this.resultImageUrl!, "_blank");
        });
    }
  }

  private resetUpload(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.selectedFile = null;
    this.previewUrl = null;
    this.resultImageUrl = null;
    this.errorMessage = null;
    this.currentJobId = null;

    if (this.sseUnsubscribe) {
      this.sseUnsubscribe();
      this.sseUnsubscribe = null;
    }
  }

  // ── Custom Style Application ────────────────

  private applyButtonStyles(): void {
    const btn = this.shadow.querySelector<HTMLElement>(".ps-button");
    if (!btn) return;

    const map: Partial<Record<keyof ButtonStyles, string>> = {
      backgroundColor: "--ps-btn-bg",
      textColor: "--ps-btn-color",
      borderRadius: "--ps-btn-radius",
      fontSize: "--ps-btn-font-size",
      fontFamily: "--ps-btn-font",
      fontWeight: "--ps-btn-font-weight",
      textDecoration: "--ps-btn-text-decoration",
      padding: "--ps-btn-padding",
      paddingLeft: "--ps-btn-padding-left",
      paddingRight: "--ps-btn-padding-right",
      paddingTop: "--ps-btn-padding-top",
      paddingBottom: "--ps-btn-padding-bottom",
      border: "--ps-btn-border",
      width: "--ps-btn-width",
      minWidth: "--ps-btn-min-width",
      maxWidth: "--ps-btn-max-width",
      height: "--ps-btn-height",
      hoverBackgroundColor: "--ps-btn-hover-bg",
      hoverTextColor: "--ps-btn-hover-color",
      iconSize: "--ps-btn-icon-size",
      iconColor: "--ps-btn-icon-color",
      boxShadow: "--ps-btn-shadow",
    };

    for (const [key, cssVar] of Object.entries(map)) {
      const value = this.buttonStyles[key as keyof ButtonStyles];
      if (value) {
        this.style.setProperty(cssVar, value);
      }
    }
  }

  private applyModalStyles(): void {
    const map: Record<keyof ModalStyles, string> = {
      overlayColor: "--ps-modal-overlay",
      backgroundColor: "--ps-modal-bg",
      textColor: "--ps-modal-color",
      borderRadius: "--ps-modal-radius",
      width: "--ps-modal-width",
      maxWidth: "--ps-modal-max-width",
      fontFamily: "--ps-modal-font",
      headerBackgroundColor: "--ps-modal-header-bg",
      headerTextColor: "--ps-modal-header-color",
      closeButtonColor: "--ps-modal-close-color",
      uploadBorderColor: "--ps-upload-border",
      uploadBackgroundColor: "--ps-upload-bg",
      uploadTextColor: "--ps-upload-color",
      uploadIconColor: "--ps-upload-icon-color",
      primaryButtonBackgroundColor: "--ps-modal-primary-bg",
      primaryButtonTextColor: "--ps-modal-primary-color",
      primaryButtonBorderRadius: "--ps-modal-primary-radius",
      loaderColor: "--ps-loader",
      resultBorderRadius: "--ps-result-radius",
      accentColor: "--ps-accent",
      accentHoverColor: "--ps-accent-hover",
      accentLightColor: "--ps-accent-light",
      textPrimaryColor: "--ps-text-primary",
      textSecondaryColor: "--ps-text-secondary",
      textMutedColor: "--ps-text-muted",
      borderColor: "--ps-border-color",
      secondaryBackgroundColor: "--ps-bg-secondary",
      errorColor: "--ps-error-color",
      successColor: "--ps-success-color",
      logoHeight: "--ps-logo-height",
    };

    for (const [key, cssVar] of Object.entries(map)) {
      const value = this.modalStyles[key as keyof ModalStyles];
      if (value) {
        this.style.setProperty(cssVar, value);
      }
    }
  }
}
